import { useCallback, useEffect, useRef } from 'react';

import {
  formatMegabytes,
  useAlert,
  useImageUpload,
} from '@spotify/confidence-core-react';

import { MAX_IMAGES_PER_TURN, MAX_IMAGE_BYTES_PER_TURN } from '../../constants';
import {
  ChatAttachment,
  ImageAttachment,
  UploadingImageAttachment,
  attachmentKey,
} from '../../types';

const isImageAttachment = (
  attachment: ChatAttachment,
): attachment is ImageAttachment | UploadingImageAttachment =>
  attachment.kind === 'image' || attachment.kind === 'uploading-image';

type UseChatImageUploadOptions = {
  attachments: ChatAttachment[];
  addAttachment: (attachment: ChatAttachment) => void;
  removeAttachment: (key: string) => void;
};

type UseChatImageUploadResult = {
  uploadImage: (file: File) => void;
  isUploading: boolean;
  removeImageAttachment: (key: string) => void;
  warnIfUploading: () => boolean;
};

export const useChatImageUpload = ({
  attachments,
  addAttachment,
  removeAttachment,
}: UseChatImageUploadOptions): UseChatImageUploadResult => {
  const { upload, isUploading } = useImageUpload();
  const alertApi = useAlert();
  const nextUploadIdRef = useRef(0);
  const previewUrlsRef = useRef(new Map<string, string>());

  const attachmentsRef = useRef(attachments);
  attachmentsRef.current = attachments;

  useEffect(() => {
    const urls = previewUrlsRef.current;
    return () => {
      urls.forEach(url => URL.revokeObjectURL(url));
      urls.clear();
    };
  }, []);

  const revokePreviewUrl = useCallback((uploadId: string) => {
    const url = previewUrlsRef.current.get(uploadId);
    if (url) {
      URL.revokeObjectURL(url);
      previewUrlsRef.current.delete(uploadId);
    }
  }, []);

  const uploadImage = useCallback(
    (file: File) => {
      const currentImages = attachmentsRef.current.filter(isImageAttachment);

      if (currentImages.length >= MAX_IMAGES_PER_TURN) {
        alertApi.post({
          severity: 'error',
          message: `You can attach up to ${MAX_IMAGES_PER_TURN} images per message.`,
        });
        return;
      }

      const usedBytes = currentImages.reduce(
        (total, a) => total + a.sizeBytes,
        0,
      );
      if (usedBytes + file.size > MAX_IMAGE_BYTES_PER_TURN) {
        alertApi.post({
          severity: 'error',
          message: `Images must total less than ${formatMegabytes(MAX_IMAGE_BYTES_PER_TURN)} per message.`,
        });
        return;
      }

      const uploadId = String(++nextUploadIdRef.current);
      const localPreviewUrl = URL.createObjectURL(file);
      const uploadingKey = `uploading:${uploadId}`;
      previewUrlsRef.current.set(uploadId, localPreviewUrl);

      const uploadingAttachment: UploadingImageAttachment = {
        kind: 'uploading-image',
        uploadId,
        fileName: file.name,
        localPreviewUrl,
        sizeBytes: file.size,
      };

      addAttachment(uploadingAttachment);

      upload(file)
        .then(imageUri => {
          const stillPresent = attachmentsRef.current.some(
            a => attachmentKey(a) === uploadingKey,
          );
          removeAttachment(uploadingKey);
          revokePreviewUrl(uploadId);

          if (!stillPresent) return;

          const imageAttachment: ImageAttachment = {
            kind: 'image',
            imageUri,
            fileName: file.name,
            sizeBytes: file.size,
          };
          addAttachment(imageAttachment);
        })
        .catch(() => {
          removeAttachment(uploadingKey);
          revokePreviewUrl(uploadId);
        });
    },
    [addAttachment, removeAttachment, upload, alertApi, revokePreviewUrl],
  );

  const removeImageAttachment = useCallback(
    (key: string) => {
      if (key.startsWith('uploading:')) {
        revokePreviewUrl(key.replace('uploading:', ''));
      }
      removeAttachment(key);
    },
    [removeAttachment, revokePreviewUrl],
  );

  const warnIfUploading = useCallback((): boolean => {
    const hasUploading = attachmentsRef.current.some(
      a => a.kind === 'uploading-image',
    );
    if (hasUploading) {
      alertApi.post({
        severity: 'warning',
        message: 'Please wait for images to finish uploading.',
      });
    }
    return hasUploading;
  }, [alertApi]);

  return { uploadImage, isUploading, removeImageAttachment, warnIfUploading };
};
